<template>
    <div class="content-wrapper">
        <!-- Page Loader -->
        <div class="vld-parent">
            <loading :active.sync="isLoading" :is-full-page="fullPage"></loading>
        </div>

        <breadcrumb :page="$t('form.setting.ipTitle')"></breadcrumb>

        <div class="content">
            <div class="container-fluid">

                <b-alert v-model="alert" :variant="alertClass" dismissible fade>{{alertMessage}}</b-alert>

                <b-row>
                    <b-col md="12">
                        <b-form-group>
                            <b-form-radio v-model="ipMode" value="block">
                                {{$t('form.setting.blockExcept')}}
                            </b-form-radio>
                            <b-form-radio v-model="ipMode" value="allow">
                                {{$t('form.setting.allowExcept')}}
                            </b-form-radio>
                        </b-form-group>
                    </b-col>

                    <b-col md="6">
                        <div class="card">
                            <div class="card-header bg-light">
                                <div class="card-title">{{$t('form.setting.ipList')}}</div>
                            </div>

                            <div class="card-body p-0">
                                <table class="table table-hover" v-if="ips.length">
                                    <thead>
                                        <tr>
                                            <th>{{$t('form.setting.ip')}}</th>
                                            <th class="text-right">{{$t('form.setting.action')}}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(ip, index) in ips" :key="index">
                                            <td>{{ip.ip}}</td>
                                            <td class="text-right">
                                                <button class="btn btn-danger btn btn-sm" @click="deleteIp(index)"
                                                    :title="$t('form.setting.deleteIP')"
                                                    v-if="permissions.deleteSettingsIps">
                                                    <span class="fa fa-trash"></span>
                                                </button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <p class="p-3 m-0" v-else>{{$t('noRecord')}}</p>
                            </div>

                            <pagination v-if="pagination.last_page > 1" :pagination="pagination" :offset="5"
                                @paginate="paginate()">
                            </pagination>
                        </div>
                    </b-col>

                    <b-col md="6">
                        <form action="#" @submit.prevent="addIp">
                            <div class="card">
                                <div class="card-header bg-light">
                                    <div class="card-title">{{$t('form.setting.ipAdd')}}</div>
                                    <b-button variant="primary" type="submit" class="float-right">
                                        {{$t('button.add')}}
                                    </b-button>
                                </div>

                                <div class="card-body">
                                    <div class="form-group">
                                        <label>{{$t('form.setting.ip')}}</label>
                                        <input type="text" class="form-control" v-model.trim="$v.ip.$model"
                                            :class="{ 'is-invalid': $v.ip.$error }"
                                            :placeholder="$t('form.setting.subnet')">
                                        <div class="invalid-feedback" v-if="!$v.ip.required">
                                            {{$t('form.validation.required')}}</div>
                                        <div class="invalid-feedback" v-if="!$v.ip.cidr">
                                            {{$t('form.validation.subnet')}}</div>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </b-col>
                </b-row>

                <b-row>
                    <b-col md="6">
                        <div class="card">
                            <div class="card-header bg-light">
                                <div class="card-title">{{$t('form.setting.blockIpList')}}</div>

                                <div class="text-right" v-if="permissions.editSettingsAutoblockip">
                                    <b-button variant="success" @click="toggleStatus" v-if="blockIPStatus">
                                        {{$t('button.enabled')}}</b-button>
                                    <b-button variant="danger" @click="toggleStatus" v-else>{{$t('button.disabled')}}
                                    </b-button>
                                </div>
                            </div>

                            <div class="card-body p-0">
                                <table class="table table-hover" v-if="blockIps.length">
                                    <thead>
                                        <tr>
                                            <th>{{$t('form.setting.blockIp')}}</th>
                                            <th>{{$t('form.setting.blockAt')}}</th>
                                            <th class="text-right">{{$t('form.setting.action')}}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(ip, index) in blockIps" :key="index">
                                            <td>{{ip.ip}}</td>
                                            <td>{{ip.updated_at | formatDate(datetimeFormat)}}</td>
                                            <td class="text-right">
                                                <button class="btn btn-danger btn btn-sm" @click="deleteBlockIp(index)"
                                                    :title="$t('form.setting.deleteIP')"
                                                    v-if="permissions.deleteSettingsIps">
                                                    <span class="fa fa-trash"></span>
                                                </button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <p class="p-3 m-0" v-else>{{$t('noRecord')}}</p>
                            </div>

                            <pagination v-if="blockIpPagination.last_page > 1" :pagination="blockIpPagination"
                                :offset="5" @paginate="paginate('block')">
                            </pagination>
                        </div>
                    </b-col>
                </b-row>
            </div>
        </div>
    </div>
</template>

<script>
    import {
        helpers,
        required
    } from "vuelidate/lib/validators";

    const cidr = helpers.regex('cidr', /^([0-9]{1,3}\.){3}[0-9]{1,3}(\/([0-9]|[1-2][0-9]|3[0-2]))?$/);

    export default {
        props: ['permissions', 'datetimeFormat'],

        data() {
            return {
                alert: false,
                alertClass: '',
                alertMessage: '',

                isLoading: false,
                fullPage: true,

                pagination: {
                    current_page: 1
                },

                blockIpPagination: {
                    current_page: 1
                },

                ip: null,
                ipMode: null,
                blockIPStatus: false,

                ips: [],
                blockIps: []
            }
        },

        validations: {
            ip: {
                required,
                cidr
            }
        },

        mounted() {
            if (!this.permissions.viewSettingsIps) {
                return this.$router.push('/');
            }

            this.getIpSettings();
            this.getAutoBlockIpSettings();
            this.getIps();
            this.getBlockIps();
        },

        watch: {
            ipMode(val, oldVal) {
                if (val) {
                    this.saveIpSettings();
                }
            }
        },

        methods: {

            getAutoBlockIpSettings() {
                axios
                    .get("/api/settings?name=auto-block-ip")
                    .then(response => {
                        this.blockIPStatus = response.data;
                    })
                    .catch(error => {
                        console.log('error fetching ip block/allow settings.');
                    });
            },

            toggleStatus() {
                this.isLoading = true;

                axios
                    .put('/api/ips/status', {})
                    .then(response => {
                        this.isLoading = false;

                        this.blockIPStatus = !this.blockIPStatus;
                        this.showAlert(response.data.message)
                    })
                    .catch(error => {
                        // console.log(error.response);
                        this.isLoading = false;
                    })
            },

            saveIpSettings() {
                this.isLoading = true;

                axios
                    .post("/api/settings?name=ip-mode", {
                        value: this.ipMode
                    })
                    .then(response => {
                        this.isLoading = false;
                    })
                    .catch(error => {
                        this.isLoading = false;
                        console.log('error updating ip block/allow settings.');
                    });
            },

            getIpSettings() {
                axios
                    .get("/api/settings?name=ip-mode")
                    .then(response => {
                        if (response.data.length > 0) this.ipMode = response.data;
                    })
                    .catch(error => {
                        console.log('error fetching ip block/allow settings.');
                    });
            },

            // Get Ips
            getIps() {
                this.isLoading = true;

                axios
                    .get('/api/ips?page=' + this.pagination.current_page)
                    .then(response => {
                        this.ips = response.data.data.data;
                        this.pagination = response.data.pagination;

                        this.isLoading = false;
                    })
                    .catch(error => {
                        console.log('error fetching ips.');

                        this.isLoading = false;
                    });
            },

            // Get Block Ips
            getBlockIps() {
                this.isLoading = true;

                axios
                    .get('/api/ips/blocked?page=' + this.blockIpPagination.current_page)
                    .then(response => {
                        this.blockIps = response.data.data.data;
                        this.blockIpPagination = response.data.pagination;

                        this.isLoading = false;
                    })
                    .catch(error => {
                        console.log('error fetching blocked ips.');

                        this.isLoading = false;
                    });
            },

            // Pagination
            paginate(type = null) {

                if (type) {
                    this.getBlockIps();
                    return;
                }

                this.getIps();
            },

            addIp() {

                this.$v.$touch();

                if (this.$v.$invalid) {
                    this.showAlert(this.$t('form.validation.error'), 'danger');
                    return;
                }

                this.isLoading = true;

                axios
                    .post('/api/ips', {
                        ip: this.ip,
                        type: null
                    })
                    .then(response => {
                        this.ip = null;
                        this.$v.$reset();

                        this.showAlert(response.data.message);
                        this.ips.unshift(response.data.data);

                        this.isLoading = false;
                    })
                    .catch(error => {
                        if (error.response.status == 422 && error.response.data.error) {
                            this.showAlert(error.response.data.message, 'danger');
                        }

                        if (error.response.data && error.response.data.errors) {
                            let errors = error.response.data.errors;

                            if (errors.ip) {
                                this.showAlert(errors.ip[0], 'danger');
                            }
                        }

                        this.isLoading = false;
                    });
            },

            deleteIp(index) {
                let conf = confirm(this.$t('form.validation.delete'));

                if (conf === true) {
                    this.isLoading = true;

                    axios
                        .delete('/api/ips/' + this.ips[index].id)
                        .then(response => {
                            if (response.status == 200) {
                                this.showAlert(response.data.message);
                                this.ips.splice(index, 1);

                                this.isLoading = false;
                            }
                        })
                        .catch(error => {
                            this.isLoading = false;

                            console.log('error');
                        });
                }
            },

            deleteBlockIp(index) {
                let conf = confirm(this.$t('form.validation.delete'));

                if (conf === true) {
                    this.isLoading = true;

                    axios
                        .delete('/api/ips/blocked/' + this.blockIps[index].id)
                        .then(response => {
                            if (response.status == 200) {
                                this.showAlert(response.data.message);
                                this.blockIps.splice(index, 1);

                                this.isLoading = false;
                            }
                        })
                        .catch(error => {
                            this.isLoading = false;

                            console.log('error');
                        });
                }
            },

            /**
             * Show alert
             */
            showAlert(message, cls = 'success') {
                this.alertMessage = message;
                this.alertClass = cls;
                this.alert = true;
            },

        }
    }

</script>
